import React from 'react'
import {
  useResponsiveDropdown,
  useBreakpoint,
  SFSymbolLabel,
} from '@sketch/components'
import { useGetSectionsForPageQuery } from '@sketch/gql-types'

import { useVersioning } from '../../../../versioning'
import { getSiblingSections } from '../../utils/getSiblingSections'
import {
  Trigger,
  CurrentSection,
  Chevrons,
  Container,
  StyledDropdownNavItem,
} from './SectionSelector.styles'

const DROPDOWN_STYLES = {
  minWidth: 160,
  overflowY: 'auto',
  maxHeight: '80vh',
} as const

interface SectionListItem {
  uuid: string
  name: string | null
}

interface SectionListProps {
  shareIdentifier: string
  pageUUID: string
  currentSectionUUID: string
  sections: SectionListItem[]
}

function SectionList({
  shareIdentifier,
  pageUUID,
  currentSectionUUID,
  sections,
}: SectionListProps) {
  const { getPathname } = useVersioning()

  return (
    <>
      {sections.map(section => {
        const pathname = getPathname({
          routeKey: 'PAGE_SECTION',
          routeParams: {
            shareID: shareIdentifier,
            pageUUID,
            sectionUUID: section.uuid,
          },
        })

        return (
          <StyledDropdownNavItem
            key={section.uuid}
            data-testid="section-selector-item"
            to={pathname}
            isActive={match =>
              Boolean(match) || section.uuid === currentSectionUUID
            }
            exact
          >
            <SFSymbolLabel text={section.name ?? ''} />
          </StyledDropdownNavItem>
        )
      })}
    </>
  )
}

interface SectionSelectorProps {
  shareIdentifier: string
  pageUUID: string
  currentSectionUUID: string
  currentSectionName?: string | null
}

export const SectionSelector: React.FC<SectionSelectorProps> = ({
  shareIdentifier,
  pageUUID,
  currentSectionUUID,
  currentSectionName,
}) => {
  const { versionShortId } = useVersioning()

  const { data } = useGetSectionsForPageQuery({
    variables: {
      identifier: shareIdentifier,
      versionShortId,
      pageUUID,
    },
  })

  // The dropdown lets you switch between the current Section's siblings; its own
  // children are reached by navigating into it, not from here.
  const allSections = data?.share?.version?.document?.page?.sections ?? []
  const sections = getSiblingSections(allSections, currentSectionUUID)
  const isMobile = !useBreakpoint('sm')

  const [content, buttonProps] = useResponsiveDropdown({
    dropdown: SectionList,
    dropdownProps: {
      shareIdentifier,
      pageUUID,
      currentSectionUUID,
      sections,
    },
    usePortal: true,
    placement: isMobile ? 'top-start' : 'bottom-start',
    dropdownStyle: DROPDOWN_STYLES,
    offset: [-8, 13], // 13 = 5 (usual offset) + 8 (canvas panel bottom padding)
  })

  const isDisabled = sections.length <= 1

  return (
    <Container>
      <Trigger
        {...buttonProps}
        aria-label="Canvas sections list"
        data-testid="sections-selector-trigger"
        disabled={isDisabled}
      >
        <CurrentSection>
          {currentSectionName ? (
            <SFSymbolLabel text={currentSectionName} />
          ) : (
            'Select section...'
          )}
        </CurrentSection>
        {!isDisabled && <Chevrons />}
      </Trigger>
      {!isDisabled && content}
    </Container>
  )
}
